import React, { useState } from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'

import { Box, Button, Tab, Tabs, Typography } from '@material-ui/core'
import { ArrowBack as ArrowLeft } from '@material-ui/icons'
import { makeStyles, Theme } from '@material-ui/core/styles'

import { TabPanel } from '../../../components/TabPanel/TabPanel'
import { CampaignSetupTab } from '../../Outbound/Campaign/CreateCampaign/CampaignSetupTab'
import { CampaignSequenceTab } from '../../Outbound/Campaign/CreateCampaign/CampaignSequenceTab'
import {
    CampaignData,
    CTAOption,
    SequenceItem,
    SequenceStep,
} from '../../Outbound/Campaign/CreateCampaign/types'
import { useCampaign } from '../../../hooks/Outreach/useCampaign'
import { Campaign } from '../../../models/Outreach/types'
import {
    useSeasonDetail,
    useSeasonIdFromQuery,
} from './hooks/useSeasonCampaigns'
import {
    leasingSeasonOutreachPath,
    seasonCampaignDetailPath,
} from './utils/seasonCampaignUtils'

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        padding: theme.spacing(3),
        maxWidth: 1200,
        margin: '0 auto',
    },
    header: {
        marginBottom: theme.spacing(3),
    },
    backButton: {
        marginBottom: theme.spacing(2),
        textTransform: 'none',
    },
    subtitle: {
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.5),
    },
    tabs: {
        marginBottom: theme.spacing(2),
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    footer: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: theme.spacing(3),
    },
}))

export const SeasonCampaignCreate: React.FC = () => {
    const classes = useStyles()
    const location = useLocation()
    const history = useHistory()
    const seasonId = useSeasonIdFromQuery(location.search)
    const seasonQuery = useSeasonDetail(seasonId)
    const season = seasonQuery.data
    const { createCampaign } = useCampaign()

    const [campaignData, setCampaignData] = useState<CampaignData>({
        name: '',
        channels: { email: true, sms: false },
        cta: 'schedule-tour',
        reply_to_email: '',
        sender_name: '',
        concessions: [],
    })
    const [sequence, setSequence] = useState<SequenceItem[]>([])
    const [activeTab, setActiveTab] = useState(0)

    const ctaOptions: CTAOption[] = [
        { value: 'schedule-tour', label: 'Schedule a Tour' },
        { value: 'request-info', label: 'Request More Info' },
        { value: 'apply-now', label: 'Apply Now' },
        { value: 'call-us', label: 'Call Us Today' },
    ]

    const isSetupComplete = () =>
        campaignData.name.trim() !== '' &&
        campaignData.reply_to_email.trim() !== '' &&
        campaignData.sender_name.trim() !== '' &&
        (campaignData.channels.email || campaignData.channels.sms)

    const isSequenceComplete = () => {
        const messageSteps = sequence.filter(
            (item): item is SequenceStep =>
                item.type === 'email' || item.type === 'sms',
        )
        return (
            messageSteps.length > 0 &&
            messageSteps.every((step) => !!step.guest_card)
        )
    }

    const handleCreate = () => {
        if (!seasonId) {
            toast.error('Season context is required')
            return
        }
        if (!isSetupComplete() || !isSequenceComplete()) {
            toast.error('Complete setup and sequence before creating')
            return
        }

        createCampaign.mutate(
            {
                name: campaignData.name.trim(),
                campaignGoal: campaignData.cta,
                sequence,
                replyToEmail: campaignData.reply_to_email.trim(),
                senderName: campaignData.sender_name.trim(),
                leasingSeasonId: seasonId,
                concessions: campaignData.concessions,
            },
            {
                onSuccess: async (response) => {
                    const campaign: Campaign = response.data
                    if (campaign?.id) {
                        history.push(
                            seasonCampaignDetailPath(campaign.id, seasonId),
                        )
                    }
                },
            },
        )
    }

    if (seasonId == null) {
        return (
            <Box className={classes.container}>
                <Typography color="textSecondary">
                    Create custom campaigns from a leasing season.
                </Typography>
                <Button
                    component={Link}
                    to={leasingSeasonOutreachPath()}
                    startIcon={<ArrowLeft />}
                    style={{ marginTop: 16 }}
                >
                    Go to Leasing Season
                </Button>
            </Box>
        )
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.header}>
                <Button
                    component={Link}
                    to={leasingSeasonOutreachPath(seasonId)}
                    startIcon={<ArrowLeft />}
                    className={classes.backButton}
                    size="small"
                >
                    Back to Leasing Season
                </Button>
                <Typography variant="h5">New custom campaign</Typography>
                <Typography variant="body2" className={classes.subtitle}>
                    {season?.name
                        ? `Add a campaign for ${season.name}. Auto-enroll rules for custom campaigns come later; this creates a manual campaign tied to the season.`
                        : 'Create a custom campaign for this leasing season.'}
                </Typography>
            </Box>

            <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                className={classes.tabs}
                indicatorColor="primary"
                textColor="primary"
            >
                <Tab label="Setup" />
                <Tab label="Message sequence" disabled={!isSetupComplete()} />
            </Tabs>

            <TabPanel value={activeTab} index={0}>
                <CampaignSetupTab
                    campaignData={campaignData}
                    setCampaignData={setCampaignData}
                    ctaOptions={ctaOptions}
                    sequence={sequence}
                    onNext={() => setActiveTab(1)}
                />
            </TabPanel>

            <TabPanel value={activeTab} index={1}>
                <CampaignSequenceTab
                    sequence={sequence}
                    setSequence={setSequence}
                    channels={campaignData.channels}
                    campaignName={campaignData.name}
                    campaignGoal={campaignData.cta}
                    onBack={() => setActiveTab(0)}
                    onNext={handleCreate}
                    backLabel="Back to setup"
                    nextLabel={
                        createCampaign.isLoading
                            ? 'Creating…'
                            : 'Create campaign'
                    }
                />
            </TabPanel>
        </Box>
    )
}
